// src/layouts/AppLayout.js
import { useCallback, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth, isGuestUser } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { useNavigate, Outlet } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import { buildCompanyPath } from "../utils/landingCompany";
import { ModalProvider } from "../context/ModalContext";
import Footer from "../components/navigation/Footer";
import Logo from "../components/logo/Logo";
import config from "../config/config";
import profilePlaceholder from "../images/profile-pics.png";
import { applyThemeCss, removeThemeCss } from '../utils/themeCss';
import api from "../api/axios";
import Toast from "../components/common/Toast";
import IdleSessionWatcher from "../components/session/IdleSessionWatcher";
import GuestDemoNotice from "../components/common/GuestDemoNotice";

export default function AppLayout() {
  const { user, logoutUser, loginAdmin } = useAuth();
  const navigate = useNavigate();
  const theme = useContext(ThemeContext);

  const [profilePic, setProfilePic] = useState(profilePlaceholder);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [isLoggingInAdmin, setIsLoggingInAdmin] = useState(false);

  const showToast = (message, type = 'success', duration = 4000) => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  };

  useEffect(() => {
    let mounted = true;
    const id = 'app-theme';

    const load = async () => {
      try {
        const cssPath = theme?.css_file_path;
        if (mounted && cssPath) {
          const href = cssPath.startsWith('http') || cssPath.startsWith('blob:')
            ? cssPath
            : `${config.assetsBaseUrl.replace(/\/$/, '')}/uploads/${String(cssPath).replace(/^\/+/, '')}`;
          applyThemeCss({ id, href, cacheBust: theme?.updated_at || Date.now() });
        }
      } catch (e) {
        // ignore
      }
    };

    load();
    return () => {
      mounted = false;
      removeThemeCss({ id });
    };
  }, [theme]);

  // ✅ Update profile pic only when user changes
  useEffect(() => {
    if (user?.profile_pic) {
      setProfilePic(
        `${process.env.REACT_APP_ASSETS_BASE_URL}/uploads/users/${user.profile_pic}?t=${Date.now()}`
      );
    } else {
      setProfilePic(profilePlaceholder);
    }
  }, [user]);

  const handleLogout = async () => {
    const isGuest = isGuestUser(user);
    // Navigate away from the protected route FIRST, before clearing auth
    // state. ProtectedRoute re-renders the instant `user` becomes null and
    // redirects to /login on its own, independent of anything this function
    // does next — since logoutUser()'s state-clearing and this function's
    // own navigate() call happen in separate async ticks (across an await),
    // ProtectedRoute's own redirect can win that race. Leaving the protected
    // route before `user` ever becomes null removes the race entirely: by
    // the time it clears, ProtectedRoute isn't mounted to react to it.
    navigate(isGuest ? "/demo-ended" : buildCompanyPath(""));
    await logoutUser();
  };

  const handleIdleTimeout = useCallback(async () => {
    const isGuest = isGuestUser(user);
    navigate(
      isGuest ? "/demo-ended" : buildCompanyPath(""),
      isGuest ? { state: { reason: "timeout" } } : undefined,
    );
    await logoutUser();
  }, [logoutUser, navigate, user]);

  // Hard limit for guest sessions: the 10+1 minute idle timeout above only
  // catches an INACTIVE guest. An ACTIVE guest who keeps interacting past
  // the token's real lifetime would instead start hitting random 401s
  // mid-task as the JWT expires server-side — this proactively ends the
  // session cleanly first. Reads the actual `exp` claim from the token
  // (guest tokens are minted with a 30-minute TTL — see GUEST_TTL_MS in the
  // API's guestSession.service.js) rather than hardcoding "28 minutes", so
  // this stays correct even if that TTL changes later.
  const HARD_LIMIT_BUFFER_MS = 2 * 60 * 1000;
  useEffect(() => {
    if (!isGuestUser(user)) return undefined;

    const token = localStorage.getItem("token");
    if (!token) return undefined;

    let expMs;
    try {
      expMs = jwtDecode(token).exp * 1000;
    } catch {
      return undefined;
    }
    if (!expMs) return undefined;

    const msUntilHardLimit = Math.max(0, expMs - HARD_LIMIT_BUFFER_MS - Date.now());
    const timer = setTimeout(() => {
      handleIdleTimeout();
    }, msUntilHardLimit);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleMasterAdminLogin = async () => {
    if (!user || user.user_type !== "M") {
      showToast("Access denied. Only master users can access admin panel.", "error");
      return;
    }

    setIsLoggingInAdmin(true);
    try {
      // Get the user's token from localStorage
      const token = localStorage.getItem("token");
      if (!token) {
        showToast("Session expired. Please log in again.", "error");
        return;
      }

      // Call the new API endpoint for master user admin access
      const res = await api.post("/admin/auth/master-login", {
        token: token
      });

      if (res.data.status === "success") {
        const { token: adminToken } = res.data;

        //  console.log("AppLayout: Master login successful, calling loginAdmin");
        // Use the existing loginAdmin function
        loginAdmin(adminToken);

        // Open dashboard in new tab without /admin prefix for master users
        console.log("AppLayout: Opening master dashboard in new tab (no /admin prefix)");
        window.open(`${window.location.origin}/master/dashboard`, '_blank');
        showToast("Accessing admin panel...", "success");
      } else {
        showToast(res.data.message || "Failed to access admin panel.", "error");
      }
    } catch (error) {
      console.error("Master admin login error:", error);
      showToast(
        error.response?.data?.message || "Failed to access admin panel. Please try again.",
        "error"
      );
    } finally {
      setIsLoggingInAdmin(false);
    }
  };


  const toCamelCase = (str) => {
    if (!str) return '';
    return str.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <ModalProvider>
      <IdleSessionWatcher
        enabled
        idleMsBeforeWarning={
          isGuestUser(user) ? config.guestIdlePromptAfterMs : config.idlePromptAfterMs
        }
        warningGraceMs={
          isGuestUser(user) ? config.guestIdlePromptGraceMs : config.idlePromptGraceMs
        }
        onTimeout={handleIdleTimeout}
        onSignOut={handleLogout}
        timerName="ibottomline-app"
      />
      <div className="application-fe">
        <style>{`
          body {
            font-family: var(--font-family-primary);
            font-size: var(--font-size14);
            font-weight: var(--font-wght400);
            color: var(--color-heading);
            background: linear-gradient(90deg, rgba(246, 248, 252, 1) 0%, rgba(246, 248, 252, 1) 50%, rgba(221, 217, 233, 1) 100%);
          }
        `}</style>

        {isGuestUser(user) && <GuestDemoNotice />}

        <div className="container-outer">
          {/* Header */}
          <div className="logo-area-header">
            <div className="logo-one">
              {/* <Logo type="primary" size={156} /> */}
              <h2>{theme.main_company_name}</h2>
            </div>
            <div className="logo-two">
              <Logo type="secondary" size={180} />
            </div>

            {/* User Menu */}
            <div className="user-area">
              <div className="logout-area">
                <div className="user-menu">
                  <ul>
                    <li>
                      <div className="dashuserpf">
                        <span>
                          {toCamelCase(user?.first_name)} {toCamelCase(user?.last_name) || ""}
                        </span>
                        <span>
                          <img
                            src={profilePic}
                            alt="Profile"
                            onError={(e) => (e.target.src = profilePlaceholder)}
                            style={{

                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </span>
                      </div>
                      <ul>
                        <li>
                          <Link to="/settings" rel="noopener noreferrer">
                            {isGuestUser(user) ? "My Settings" : "My Account"}
                          </Link>
                        </li>
                        {user?.user_type === "M" && (
                          <li>
                            <Link
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault();
                                handleMasterAdminLogin();
                              }}
                              style={{ pointerEvents: isLoggingInAdmin ? 'none' : 'auto' }}
                            >
                              {isLoggingInAdmin ? 'Accessing Admin...' : 'Account Settings'}
                            </Link>
                          </li>
                        )}
                        <li>
                          <Link rel="noopener noreferrer" onClick={(e) => {
                            e.preventDefault();
                            handleLogout();
                          }}>Log Out</Link>

                        </li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="inner-container">
            <Outlet />
          </div>

          {/* Footer */}
          <Footer />
        </div>
      </div>

      {/* Toast Notifications */}
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, visible: false })}
        />
      )}
    </ModalProvider>
  );
}
